import {
  type CostsSummary,
  OPPORTUNITY_STATUSES,
  type OpportunityStatus,
  type YearMonth,
} from '@radar/contracts';
import { queryOptions } from '@tanstack/react-query';
import type { InferRequestType } from 'hono/client';
import { api, apiUrl, call } from '@/lib/api';
import {
  type AuditFilters,
  type ExclusionsFilters,
  type LedgerFilters,
  qk,
} from '@/lib/query-keys';

// Appels typés des routes de configuration et de gouvernance (`api.md`) consommées par `/settings`.

export type UpdateBudgetInput = InferRequestType<typeof api.settings.budget.$put>['json'];
export type UpdateNotificationsInput = InferRequestType<
  typeof api.settings.notifications.$put
>['json'];
export type CreateScoringConfigInput = InferRequestType<
  (typeof api)['scoring-configs']['$post']
>['json'];
export type RescoreInput = InferRequestType<
  (typeof api)['scoring-configs']['rescore']['$post']
>['json'];
export type CreateExclusionInput = InferRequestType<typeof api.exclusions.$post>['json'];
export type SetRoleInput = InferRequestType<
  (typeof api.admin.users)[':id']['role']['$post']
>['json'];
export type BanUserInput = InferRequestType<
  (typeof api.admin.users)[':id']['ban']['$post']
>['json'];
export type CreateInvitationInput = InferRequestType<typeof api.admin.invitations.$post>['json'];

export const settingsQueryOptions = () =>
  queryOptions({ queryKey: qk.settings, queryFn: () => call(api.settings.$get()) });

export const updateBudget = (json: UpdateBudgetInput) => call(api.settings.budget.$put({ json }));

export const updateNotifications = (json: UpdateNotificationsInput) =>
  call(api.settings.notifications.$put({ json }));

export const scoringConfigsQueryOptions = () =>
  queryOptions({
    queryKey: qk.scoringConfigs,
    queryFn: () => call(api['scoring-configs'].$get()),
  });

export const createScoringConfig = (json: CreateScoringConfigInput) =>
  call(api['scoring-configs'].$post({ json }));

export const activateScoringConfig = (id: string) =>
  call(api['scoring-configs'][':id'].activate.$post({ param: { id } }));

export const requestRescore = (json: RescoreInput) =>
  call(api['scoring-configs'].rescore.$post({ json }));

/** Statuts pris en compte par `rescore_all` (tout sauf `archived` et `merged`). */
export const RESCORE_STATUSES: OpportunityStatus[] = OPPORTUNITY_STATUSES.filter(
  (status) => status !== 'archived' && status !== 'merged',
);

/**
 * Nombre d'opportunités concernées par un rescoring : `GET /opportunities?pageSize=1` (route de
 * T22, pas encore typée dans `AppType`) → `total`. Absente, le dialogue affiche « indisponible ».
 */
export const rescoreCountQueryOptions = () =>
  queryOptions({
    queryKey: qk.opportunities.rescoreCount,
    queryFn: async () => {
      const url = new URL(apiUrl('/opportunities', { pageSize: '1' }));
      for (const status of RESCORE_STATUSES) url.searchParams.append('status', status);
      const body = await call<{ total: number }>(fetch(url, { credentials: 'include' }));
      return body.total;
    },
    retry: false,
    staleTime: 30_000,
    meta: { toast: false },
  });

export const exclusionsQueryOptions = (query: ExclusionsFilters) =>
  queryOptions({
    queryKey: qk.exclusions.list(query),
    queryFn: async () => (await call(api.exclusions.$get({ query }))).items,
  });

export const createExclusion = (json: CreateExclusionInput) => call(api.exclusions.$post({ json }));

export const deleteExclusion = (id: string) =>
  call(api.exclusions[':id'].$delete({ param: { id } }));

export const costsSummaryQueryOptions = (month: YearMonth) =>
  queryOptions({
    queryKey: qk.costs.summary(month),
    queryFn: (): Promise<CostsSummary> => call(api.costs.summary.$get({ query: { month } })),
    staleTime: 300_000,
  });

export const costsLedgerQueryOptions = (query: LedgerFilters) =>
  queryOptions({
    queryKey: qk.costs.ledger(query),
    queryFn: () => call(api.costs.ledger.$get({ query })),
  });

export const auditQueryOptions = (query: AuditFilters) =>
  queryOptions({ queryKey: qk.audit(query), queryFn: () => call(api.audit.$get({ query })) });

export const adminUsersQueryOptions = () =>
  queryOptions({
    queryKey: qk.admin.users,
    queryFn: async () => (await call(api.admin.users.$get())).items,
  });

export const invitationsQueryOptions = () =>
  queryOptions({
    queryKey: qk.admin.invitations,
    queryFn: async () => (await call(api.admin.invitations.$get())).items,
  });

export const setUserRole = (id: string, json: SetRoleInput) =>
  call(api.admin.users[':id'].role.$post({ param: { id }, json }));

export const banUser = (id: string, json: BanUserInput) =>
  call(api.admin.users[':id'].ban.$post({ param: { id }, json }));

export const setTemporaryPassword = (id: string) =>
  call(api.admin.users[':id']['set-password'].$post({ param: { id } }));

export const createInvitation = (json: CreateInvitationInput) =>
  call(api.admin.invitations.$post({ json }));

export const deleteInvitation = (id: string) =>
  call(api.admin.invitations[':id'].$delete({ param: { id } }));
